import { css } from "@emotion/react"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { useCallback, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { formattedCents } from "source/giving/utils/money"
import { Icon } from "source/shared/components"
import { Modal, useOpenModal } from "source/shared/components/Modal"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import {
  DonationFormInvariantError,
  DonationFormSubmissionError,
  RetryableDonationFormInvariantError,
  genericRetryableErrorMessage,
} from "../errors"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import {
  createStripeConfirmationToken,
  sendNondeterministicErrorNotifications,
} from "../queries"
import { clearLocalStorage } from "../stores/local_storage"
import { hasErrors, validate } from "../stores/validations"
import {
  Designation,
  FormSubmissionResult,
  RepeatableFrequency,
  State,
  StripeToken,
  Total,
} from "../types"
import { FormSubmission } from "../utils/FormSubmission"
import { useBugsnagClient } from "source/Bugsnag"
import { Bugsnag } from "@bugsnag/js"
import { PrimaryButton } from "./display/PrimaryButton"

type FormSubmitProps = {
  state: State
  totalCents: Total
  calculatedDesignations: Designation[]
}

export function FormSubmit({
  state,
  totalCents,
  calculatedDesignations,
}: FormSubmitProps) {
  const {
    current_person: {
      data: {
        attributes: { email_address: currentPersonEmail },
      },
    },
    current_organization: { id: currentOrganizationId },
  } = useContext(ServerRenderedProps)
  const { config, dispatch } = useDonationFormContext()
  const navigate = useNavigate()
  const stripe = useStripe()
  const stripeElements = useElements()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const bugsnagClient: Bugsnag.Client = useBugsnagClient()
  const openModal = useOpenModal()

  const showError = useCallback(
    (message: string) => {
      setErrorMessage(message)
      openModal()
    },
    [openModal],
  )

  const obtainStripeToken = async (): Promise<StripeToken> => {
    if (!stripe || !stripeElements) {
      throw new RetryableDonationFormInvariantError()
    } else if (state.stripePaymentMethod?.id) {
      return state.stripePaymentMethod.id
    } else {
      const stripeConfirmationToken = await createStripeConfirmationToken({
        stripe,
        stripeElements,
        billingEmail: currentPersonEmail,
        organizationId: currentOrganizationId,
      })
      return stripeConfirmationToken.id
    }
  }

  const handleSubmit = async () => {
    setErrorMessage(undefined)

    // Need to check for errors now, not wait for revalidation
    const currentErrors = validate(state, config)
    if (hasErrors(currentErrors)) {
      // Make sure the errors get displayed
      dispatch({ type: "VALIDATE_SUBMIT", config })
      return
    }

    setIsSubmitting(true)

    try {
      const stripeToken = await obtainStripeToken()
      const submission = FormSubmission.create({
        state,
        calculatedDesignations,
        stripeToken,
      })
      const result = await submission.submit()
      handleSubmitSuccess(result)
    } catch (error) {
      handleSubmitError(error)
    }
  }

  const handleSubmitSuccess = (result: FormSubmissionResult) => {
    clearLocalStorage()
    if (result.recurring) {
      navigate("donation_thank_you", {
        state: {
          recurring: true,
          date: (state.frequency as RepeatableFrequency).starting,
        },
      })
    } else if (result.delayed) {
      navigate("donation_processing", {
        state: { nondeterministicError: false },
      })
    } else {
      dispatch({
        type: "START_POLLING_DONATION_STATUS",
        donationId: result.id,
      })
    }
  }

  const handleSubmitError = (error: Error | unknown) => {
    setIsSubmitting(false)

    if (error instanceof DonationFormSubmissionError) {
      const { message, path, responseStatus } =
        error as DonationFormSubmissionError
      if (responseStatus == "422" || responseStatus === "429") {
        showError(message)
      } else {
        handleNondeterministicError({ path, responseStatus })
      }
    } else if (error instanceof Error) {
      showError(genericRetryableErrorMessage)
      bugsnagClient.notify(error)
    } else {
      showError(genericRetryableErrorMessage)
      bugsnagClient.notify(
        new DonationFormInvariantError("Unknown donation form submit error"),
      )
    }
  }

  const handleNondeterministicError = async ({
    path,
    responseStatus,
  }: {
    path: string
    responseStatus: string
  }) => {
    try {
      await sendNondeterministicErrorNotifications({ path, responseStatus })
    } catch (error) {
      bugsnagClient.notify(error)
    } finally {
      navigate("donation_processing", {
        state: { nondeterministicError: true },
      })
    }
  }

  return (
    <>
      <PrimaryButton
        type="submit"
        onClick={handleSubmit}
        isLoading={isSubmitting}
      >
        Give{totalCents > 0 && ` ${formattedCents(totalCents)}`}
      </PrimaryButton>
      <ErrorModal
        message={errorMessage}
        onClose={() => setErrorMessage(undefined)}
      />
    </>
  )
}

function ErrorModal({
  message,
  onClose,
}: {
  message: React.ReactNode
  onClose?: () => void
}) {
  return (
    <Modal.Show onClose={onClose}>
      <div className="pt-1 px-4 pb-3">
        <Modal.Header>
          <header className="d-f fd-c ai-c g-3">
            <Icon
              symbol="general#exclamation-triangle"
              className="c-ruby"
              aria-hidden
              css={css`
                width: 3rem;
                height: 3rem;
              `}
            />
            <span className="ta-c">Sorry, something went wrong</span>
          </header>
        </Modal.Header>
        <Modal.Body>
          <p className="ta-c m-0">{message}</p>
        </Modal.Body>
        <footer className="d-f jc-c">
          <Modal.Close className="btn neutral-btn" onClose={onClose}>
            OK
          </Modal.Close>
        </footer>
      </div>
    </Modal.Show>
  )
}
